/**
 * Doctor cotation assignments — superadmin configures which NGAP acts
 * each doctor (or specialty) can use.
 *
 * Rules:
 * - Default = all acts of the doctor's specialty
 * - Superadmin can ADD/REMOVE specific acts per doctor
 * - Superadmin can MODIFY coefficient overrides
 * - When a doctor creates an e-note, only their assigned acts are loaded
 */

import { loadAllActs, type NgapAct } from "@/lib/ngap-catalogue";

export interface DoctorCotation {
  doctorId: string;
  doctorName: string;
  specialty: string;
  defaultAllowedActs: string[]; // NGAP codes allowed by default for this specialty
  customAssignments: Array<{
    code: string;
    customCoefficient?: number;
    customBasePriceCents?: number;
    note?: string;
  }>;
  restricted: string[]; // codes explicitly forbidden
}

export const MOCK_DOCTORS = [
  { id: "dr-001", name: "Dr Ali BENABDERRAZZAK", specialty: "ANES", rpps: "12345678901", inpe: "12345678", cabinet: "Clinique Essada", city: "Safi" },
  { id: "dr-002", name: "Dr Sophie MARTIN", specialty: "CARD", rpps: "23456789012", inpe: "23456789", cabinet: "Cabinet Cardiologie Atlas", city: "Casablanca" },
  { id: "dr-003", name: "Dr Mehdi EL FASSI", specialty: "NEURO", rpps: "34567890123", inpe: "34567890", cabinet: "Clinique du Souk", city: "Fès" },
  { id: "dr-004", name: "Dr Yasmine AMRANI", specialty: "GYN", rpps: "45678901234", inpe: "45678901", cabinet: "Maternité Yasmine", city: "Rabat" },
  { id: "dr-005", name: "Dr Hassan BERRADA", specialty: "PEDI", rpps: "56789012345", inpe: "56789012", cabinet: "Cabinet Pédiatrique", city: "Marrakech" },
  { id: "dr-006", name: "Dr Leila TAZI", specialty: "OPHT", rpps: "67890123456", inpe: "67890123", cabinet: "Centre Ophtalmo", city: "Tanger" },
  { id: "dr-007", name: "Dr Omar CHRAIBI", specialty: "IMAG", rpps: "78901234567", inpe: "78901234", cabinet: "Cabinet Radiologie", city: "Casablanca" },
];

// Default specialty → NGAP codes
export const SPECIALTY_DEFAULT_ACTS: Record<string, string[]> = {
  MED: ["C", "V", "VL", "K5"],
  CARD: ["C", "CS", "CSC", "K5", "K20", "K30"],
  NEURO: ["C", "CS", "K5", "K20"],
  GYN: ["C", "CS", "K40", "K50", "K60", "K80", "Z30", "Z40", "Z50"],
  OBST: ["C", "CS", "K60", "K80", "Z30", "Z40", "Z50", "SF"],
  PEDI: ["C", "CS", "K5", "K20"],
  OPHT: ["C", "CSO", "Z10"],
  ORL: ["C", "CS", "K40", "Z10"],
  ORTH: ["C", "CS", "K5", "Z10", "Z20"],
  PSY: ["C", "C2"],
  PNEU: ["C", "CS", "K10", "Z10"],
  URG: ["C", "V", "K5", "K20", "Z10", "Z20", "Z60"],
  ANES: ["C", "CS", "K40", "K50", "K60", "K80", "Z60"],
  ENDO: ["C", "CS", "B20", "B30", "B40", "B50"],
  HEMA: ["C", "CS", "B20", "B70"],
  INFE: ["C", "CS", "B20", "B30", "B60"],
  NEPH: ["C", "CS", "B70", "B30"],
  ONCO: ["C", "CS", "B20", "B30", "B50", "B60", "Z60", "Z80"],
  RHUM: ["C", "CS", "K5", "Z10", "Z20"],
  URO: ["C", "CS", "K50", "K40", "Z10", "Z20", "Z60"],
  IMAG: ["Z10", "Z20", "Z30", "Z40", "Z50", "Z60", "Z80"],
  LAB: ["B20", "B30", "B40", "B50", "B60", "B70", "B80"],
};

// Mock cotation assignments (in production: comes from /api/admin/cotations)
export const MOCK_COTATIONS: DoctorCotation[] = MOCK_DOCTORS.map((d) => ({
  doctorId: d.id,
  doctorName: d.name,
  specialty: d.specialty,
  defaultAllowedActs: SPECIALTY_DEFAULT_ACTS[d.specialty] ?? ["C"],
  customAssignments: [],
  restricted: [],
}));

export function getDoctorCotation(doctorId: string): DoctorCotation | undefined {
  return MOCK_COTATIONS.find((c) => c.doctorId === doctorId);
}

/**
 * The acts a doctor is allowed to use.
 *
 * Asynchronous since the canonical catalogue is loaded lazily. The act
 * codes of the mock cotations are the legacy vocabulary, so they are
 * resolved through the Specialty Registry.
 */
export async function getActsForDoctor(doctorId: string): Promise<NgapAct[]> {
  const cotation = getDoctorCotation(doctorId);
  if (!cotation) return [];
  const allowedCodes = [...cotation.defaultAllowedActs, ...cotation.customAssignments.map((a) => a.code)];
  const all = await loadAllActs();
  return all.filter((a) => allowedCodes.includes(a.code) && !cotation.restricted.includes(a.code));
}

export const SPECIALTY_LABEL: Record<string, string> = {
  MED: "Médecine générale",
  CARD: "Cardiologie",
  NEURO: "Neurologie",
  GYN: "Gynécologie",
  OBST: "Obstétrique",
  PEDI: "Pédiatrie",
  OPHT: "Ophtalmologie",
  ORL: "ORL",
  ORTH: "Orthopédie",
  PSY: "Psychiatrie",
  PNEU: "Pneumologie",
  URG: "Urgences",
  ANES: "Anesthésie-Réanimation",
  ENDO: "Endocrinologie",
  HEMA: "Hématologie",
  INFE: "Infectiologie",
  NEPH: "Néphrologie",
  ONCO: "Oncologie",
  RHUM: "Rhumatologie",
  URO: "Urologie",
  IMAG: "Imagerie médicale",
  LAB: "Biologie médicale",
};
